import { Check, Trash2, TrendingDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import type { HandicapEntry } from '../lib/me.ts';
import { INDEX_DEFAUT, addHandicap, deleteHandicap, formatHandicap, getHandicap } from '../lib/me.ts';

/* L'index du joueur, saisi à la main, suivi dans le temps.
 *
 * ── Pourquoi il n'est pas calculé ───────────────────────────────────────────
 *
 * Le World Handicap System réclame le Course Rating et le Slope de chaque
 * départ, des cartes attestées par un marqueur, et un ajustement des
 * conditions du jour établi sur toutes les cartes rendues ce jour-là. Aucun
 * de ces trois n'est à la portée d'une application isolée — et la carte d'un
 * parcours compact comme Veigy ne porte même pas de Course Rating, parce que
 * ce type de parcours n'est pas classé.
 *
 * Le joueur recopie donc son index fédéral. L'écran le dit, pour que personne
 * ne prenne ce chiffre pour un calcul.
 *
 * ── Pourquoi chaque saisie ajoute une ligne ─────────────────────────────────
 *
 * C'est le MOUVEMENT qui intéresse : passer de 28 à 24 en une saison est ce
 * qu'on veut voir. Écraser la valeur précédente effacerait le chemin. Corriger
 * une faute de frappe consiste donc à supprimer la ligne, pas à la modifier.
 */

const dayLabel = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** La courbe de l'index, du plus ancien au plus récent.
 *
 *  L'axe est INVERSÉ : un index qui baisse est un joueur qui progresse, et une
 *  courbe qui descendrait pour dire « mieux » se lirait à contresens. Ici, ça
 *  monte quand on progresse. */
function Courbe({ points }: { points: HandicapEntry[] }) {
    if (points.length < 2) return null;
    const W = 320;
    const H = 90;
    const vals = points.map((p) => p.indexValue);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    // Une plage minimale : sans elle, une variation de 0,2 point remplirait
    // toute la hauteur et ferait passer un détail pour un bouleversement.
    const etendue = Math.max(2, max - min);
    const centre = (min + max) / 2;
    const bas = centre - etendue / 2;
    const x = (i: number) => (i / (points.length - 1)) * W;
    const y = (v: number) => H - ((v - bas) / etendue) * H;
    const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.indexValue)}`).join(' ');
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="mt-3 h-auto w-full" role="img"
            aria-label={`Évolution de l'index, de ${formatHandicap(points[0]!.indexValue)} à ${formatHandicap(points[points.length - 1]!.indexValue)}`}>
            <path d={d} fill="none" stroke="var(--chart-current)" strokeWidth={2.5}
                strokeLinejoin="round" strokeLinecap="round" />
            {points.map((p, i) => (
                <circle key={p.id} cx={x(i)} cy={y(p.indexValue)} r={4}
                    fill="var(--chart-current)" stroke="var(--color-base-200)" strokeWidth={2} />
            ))}
        </svg>
    );
}

export function HandicapCard() {
    const [current, setCurrent] = useState<HandicapEntry | null>(null);
    const [history, setHistory] = useState<HandicapEntry[]>([]);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deplie, setDeplie] = useState(false);
    // L'index voyage dans la session : sans ce rafraichissement, la pastille
    // de son propre avatar garderait l'ancienne valeur jusqu'au rechargement.
    const { refreshUser } = useAuth();

    const charger = (): void => {
        void getHandicap()
            .then((h) => { setCurrent(h.current); setHistory(h.history); })
            .catch(() => { /* l'écran reste utilisable sans l'historique */ });
    };
    useEffect(charger, []);

    const enregistrer = async (): Promise<void> => {
        // La virgule est la décimale française : refuser « 24,3 » obligerait à
        // taper un point que le clavier numérique d'un téléphone ne propose pas
        // toujours.
        const v = Number(draft.replace(',', '.').replace('+', '-'));
        if (!Number.isFinite(v)) { setError('Index invalide'); return; }
        setBusy(true); setError(null);
        try {
            await addHandicap(v);
            setDraft('');
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
            charger();
            void refreshUser();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
        } finally { setBusy(false); }
    };

    const supprimer = async (id: string): Promise<void> => {
        setBusy(true);
        try { await deleteHandicap(id); charger(); void refreshUser(); } catch { /* rien */ }
        finally { setBusy(false); }
    };

    // Du plus ancien au plus récent pour la courbe : le serveur rend l'inverse,
    // parce que la liste, elle, se lit du plus récent.
    const chronologie = [...history].reverse();
    const ecart = history.length >= 2
        ? history[0]!.indexValue - history[history.length - 1]!.indexValue
        : null;

    // Un index sous 5 tape sans signe : soit un tres bon joueur qui a oublié le
    // plus, soit une faute de frappe. Dans les deux cas la question mérite
    // d'être posée ; au-dessus, elle ne se pose pas.
    const brut = Number(draft.trim().replace(',', '.'));
    const ambigu = draft.trim() !== '' && !draft.includes('+') && !draft.includes('-')
        && Number.isFinite(brut) && brut > 0 && brut < 5;

    return (
        <section className="rounded-box bg-base-200 p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
                <TrendingDown size={16} /> Mon index
            </h2>

            <p className="mt-3 flex items-baseline gap-3">
                <span className={`text-4xl font-bold tabular-nums ${current ? 'text-primary' : 'opacity-50'}`}>
                    {formatHandicap(current ? current.indexValue : INDEX_DEFAUT)}
                </span>
                <span className="text-xs opacity-70">
                    {current ? `depuis le ${dayLabel(current.recordedAt)}` : 'par défaut'}
                </span>
            </p>

            {ecart != null && Math.abs(ecart) >= 0.1 && (
                <p className="mt-1 text-sm">
                    <span className={`font-bold ${ecart < 0 ? 'text-success' : 'opacity-70'}`}>
                        {ecart < 0 ? '−' : '+'}{Math.abs(ecart).toFixed(1).replace('.', ',')}
                    </span>
                    <span className="opacity-70">
                        {' '}depuis votre première saisie
                        {ecart < 0 ? ' — vous progressez.' : '.'}
                    </span>
                </p>
            )}

            <Courbe points={chronologie} />

            <div className="mt-3 flex gap-2">
                <input
                    type="text"
                    inputMode="decimal"
                    aria-label="Nouvel index"
                    className="input input-bordered h-14 flex-1 text-base tabular-nums"
                    placeholder={formatHandicap(current ? current.indexValue : INDEX_DEFAUT)}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void enregistrer(); }}
                />
                <button type="button" disabled={busy || !draft.trim()}
                    onClick={() => void enregistrer()}
                    className="btn btn-primary h-14 tap-target">
                    {current ? 'Mettre à jour' : 'Enregistrer'}
                </button>
            </div>
            {/* La convention du plus ne s'affiche que pour celui qu'elle
                concerne : un chiffre bas saisi sans signe. Affichée en
                permanence, c'est une notice de plus à lire au soleil pour les
                99 % de joueurs qui rendent des coups. */}
            {ambigu && (
                <p className="mt-1 text-xs opacity-70">
                    Mieux que le scratch&nbsp;? Saisissez «&nbsp;+{draft.trim()}&nbsp;».
                </p>
            )}

            {saved && (
                <p className="mt-2 flex items-center gap-1 text-sm text-success">
                    <Check size={14} /> Index enregistré.
                </p>
            )}
            {error && <p className="mt-2 text-sm text-error">{error}</p>}

            {history.length > 1 && (
                <>
                    <button type="button" onClick={() => setDeplie((v) => !v)}
                        aria-expanded={deplie}
                        className="btn btn-ghost btn-sm mt-2">
                        {deplie ? 'Masquer' : 'Voir'} l'historique ({history.length})
                    </button>
                    {deplie && (
                        <ul className="mt-1 space-y-1 border-t border-base-300 pt-2">
                            {history.map((h) => (
                                <li key={h.id} className="flex items-center justify-between gap-3 text-sm">
                                    <span className="tabular-nums">
                                        <span className="font-semibold">{formatHandicap(h.indexValue)}</span>
                                        <span className="ml-2 text-xs opacity-60">{dayLabel(h.recordedAt)}</span>
                                    </span>
                                    {/* Corriger une faute de frappe, c'est
                                        supprimer la ligne : les valeurs ne se
                                        modifient pas, l'historique doit rester
                                        le reflet de ce qui a été saisi. */}
                                    <button type="button" disabled={busy}
                                        aria-label={`Supprimer la saisie du ${dayLabel(h.recordedAt)}`}
                                        onClick={() => void supprimer(h.id)}
                                        className="btn btn-ghost btn-xs btn-square text-error">
                                        <Trash2 size={14} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </section>
    );
}
